// Quantity pricing on the product page and in quick view (snippets/kov-qty-pricing.liquid):
// - the price for the chosen quantity ("£2.94 each for 30 (2% off) · Total £88.20") [data-kov-qty-price]
// - discount labels, one per tier, the tier of the chosen quantity highlighted [data-kov-tiers]
// Both read the discount tiers from the quantity break rules of the B2B pricing app (BSS), for the logged in
// customer and the current product and variant, or else from the app's discount table on the page (found by its
// heading). The price comes from the price box; data-price-box, data-quantity and data-form hold the ids of the
// price box, the quantity input and the product form. Texts come from the "Quantity pricing" theme settings.
(() => {
  if (window.kovQtyPricing) return;
  window.kovQtyPricing = true;

  // Our own lines also contain "%", so they are left out when looking for the app's table
  const OWN = '[data-kov-qty-price], [data-kov-tiers]';
  const cents = (text) => {
    const n = parseFloat((text || '').replace(/[^0-9.,-]/g, '').replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  const percents = (el) => {
    let n = (el.innerText.match(/%/g) || []).length;
    el.querySelectorAll(OWN).forEach((own) => { n -= (own.innerText.match(/%/g) || []).length; });
    return n;
  };

  // The quick view looks only inside itself; the product page leaves any open quick view out
  const scopeOf = (el) => el.closest('.quickView') || document;
  const inScope = (node, scope) => (scope === document ? !node.closest('.quickView') : true);

  const findTable = (heading, scope) => {
    const wanted = heading.trim().toLowerCase();
    const label = [...scope.querySelectorAll('th, td, div, span, p, strong, b')]
      .find((el) => !el.children.length && !el.closest(OWN) && inScope(el, scope) && el.textContent.trim().toLowerCase() === wanted);
    if (!label) return null;
    let box = label.parentElement;
    while (box && !percents(box)) box = box.parentElement;
    while (box && box.parentElement && percents(box.parentElement) > percents(box) && box.parentElement.innerText.length < 3000) box = box.parentElement;
    // a table body or row is not something to hide or place things next to
    return box?.closest('table') || box;
  };

  // A tier is { min, max, type, value } with the app's discount types:
  // 0 / 6 = fixed price, 1 = fixed amount off, 2 = percentage off (others raise the price and are left out)
  const FIXED_PRICE = [0, 6];
  const AMOUNT_OFF = 1;
  const PERCENT_OFF = 2;
  const rate = () => Number(window.Shopify?.currency?.rate || 1);
  // price of one after the tier's discount, in cents
  const discounted = (price, tier) => {
    if (tier.type === PERCENT_OFF) return Math.round(price * (1 - tier.value / 100));
    if (tier.type === AMOUNT_OFF) return Math.max(0, Math.round(price - tier.value * 100 * rate()));
    if (FIXED_PRICE.includes(tier.type)) return Math.min(price, Math.round(tier.value * 100 * rate()));
    return price;
  };
  const discountText = (price, tier, money) => (tier.type === PERCENT_OFF
    ? +tier.value.toFixed(2) + '%'
    : money.format((price - discounted(price, tier)) / 100));

  // Tiers from the app's quantity break rules for this customer, product and variant
  const ruleTiers = (el) => {
    const apply = window.BSS_B2B?.integrateUtils?.getApplyQbRules;
    if (typeof apply !== 'function') return [];
    const d = el.dataset;
    const variantId = document.getElementById(d.form)?.querySelector('input[name="id"]')?.value;
    const item = {
      productId: d.productId,
      variantId,
      collectionIds: (d.collections || '').split(',').filter(Boolean),
      tags: (d.tags || '').split('|').filter(Boolean),
    };
    try { apply([item]); } catch (e) { return []; }
    const rule = item.applyQB;
    if (!rule || Number(rule.rule_setting) !== 0 || !rule.qty_table?.length) return [];
    return rule.qty_table
      .map((row) => {
        const min = Number(row.qty_from) || 1;
        const to = Number(row.qty_to);
        return { min, max: to >= min ? to : Infinity, type: Number(row.discount_type), value: Number(row.discount_value) };
      })
      .filter((tier) => tier.type === PERCENT_OFF || tier.type === AMOUNT_OFF || FIXED_PRICE.includes(tier.type));
  };

  // Tiers from the app's table on the page: "5 - 15  1.5%", "≥ 61  4%", "61+  4%"
  const tableTiers = (table) => {
    if (!table) return [];
    const lines = table.innerText.replace(/\n(?=\s*[\d.]+\s*%)/g, ' ').split('\n');
    const tiers = [];
    for (const line of lines) {
      let m = line.match(/(\d+)\s*[-–]\s*(\d+)\D+?([\d.]+)\s*%/);
      if (m) { tiers.push({ min: +m[1], max: +m[2], type: PERCENT_OFF, value: +m[3] }); continue; }
      m = line.match(/(?:≥|>=|over|above|from)\s*(\d+)\D+?([\d.]+)\s*%/i) || line.match(/(\d+)\s*\+\D*?([\d.]+)\s*%/);
      if (m) tiers.push({ min: +m[1], max: Infinity, type: PERCENT_OFF, value: +m[2] });
    }
    return tiers;
  };

  const tiersFor = (el, table) => {
    const tiers = ruleTiers(el);
    return (tiers.length ? tiers : tableTiers(table)).sort((a, b) => a.min - b.min);
  };

  // Price shown for one pack: the lowest price in the price box (the struck-through one is higher)
  const shownPrice = (boxId) => {
    const box = document.getElementById(boxId);
    if (!box) return null;
    const copy = box.cloneNode(true);
    copy.querySelectorAll('.kov-unit-price').forEach((el) => el.remove());
    const prices = (copy.textContent.match(/[£$€]\s?[\d,]+(?:\.\d+)?/g) || []).map(cents).filter((n) => n);
    return prices.length ? Math.min(...prices) : null;
  };

  const quantityOf = (el) => parseInt(document.getElementById(el.dataset.quantity)?.value, 10) || 1;
  const moneyFor = (el) => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: el.dataset.currency });
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.split('[' + key + ']').join(value), template || '');
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  const renderLine = (line) => {
    const quantity = quantityOf(line);
    const tier = tiersFor(line, findTable(line.dataset.heading, scopeOf(line))).find((t) => quantity >= t.min && quantity <= t.max);
    const price = shownPrice(line.dataset.priceBox);
    if (!tier || !price) { if (!line.hidden) line.hidden = true; return; }
    const money = moneyFor(line);
    const each = discounted(price, tier);
    const text = fill(line.dataset.template, {
      price: money.format(each / 100), qty: quantity, discount: discountText(price, tier, money), total: money.format(each * quantity / 100),
    });
    if (line.textContent !== text) line.textContent = text;
    if (line.hidden) line.hidden = false;
  };

  // The app's whole table block: the table with its own title and the line above it. Climbs from the table while
  // the parent holds nothing else (no form fields, no labels of ours, little extra text), stopping at the column.
  const STOP = 'form, .productView-details, .productView-product, [bss-b2b-main-product-form], [bss-b2b-quickview-product-form]';
  const appBlock = (table) => {
    let el = table;
    while (el.parentElement && !el.parentElement.matches(STOP) &&
      !el.parentElement.querySelector(OWN + ', select, input:not([type="hidden"]), quantity-input, quantity-quick-view-input') &&
      el.parentElement.textContent.trim().length <= el.textContent.trim().length + 200) el = el.parentElement;
    return el;
  };

  // Hide the app's table the moment the app adds it (mutation callbacks run before the page is painted), when the
  // labels replace it in that product (data-hide-table)
  const hideAddedTables = (root) => {
    if (root.nodeType !== 1) return;
    const tables = root.matches('table') ? [root] : root.querySelectorAll('table');
    tables.forEach((table) => {
      if (table.closest(OWN)) return;
      const scope = scopeOf(table);
      const labels = [...scope.querySelectorAll('[data-kov-tiers][data-hide-table="true"]')].filter((box) => inScope(box, scope));
      const text = table.textContent.toLowerCase();
      if (labels.some((box) => text.includes(box.dataset.heading.trim().toLowerCase())) && text.includes('%')) {
        appBlock(table).classList.add('kov-tiers-source');
      }
    });
  };

  const renderTiers = (box) => {
    const d = box.dataset;
    const table = findTable(d.heading, scopeOf(box));
    const tiers = tiersFor(box, table);
    const price = shownPrice(d.priceBox);
    if (!tiers.length || !price) { if (!box.hidden) box.hidden = true; return; }
    if (table) {
      // sit right after the app's table (hidden but kept for reading), outside any wrapper that only
      // holds the table, so the labels get the full width of the column; labels placed by a block (data-stay) stay put
      let anchor = table;
      while (anchor.parentElement && !anchor.parentElement.matches('form, [bss-b2b-main-product-form], [bss-b2b-quickview-product-form], .productView-details') &&
        [...anchor.parentElement.children].filter((c) => !c.matches(OWN) && c.tagName !== 'STYLE' && c.tagName !== 'SCRIPT').length === 1) anchor = anchor.parentElement;
      if (d.stay !== 'true' && anchor.nextElementSibling !== box) anchor.after(box);
      appBlock(table).classList.toggle('kov-tiers-source', d.hideTable === 'true');
    }
    const money = moneyFor(box);
    const quantity = quantityOf(box);
    const list = [...tiers];
    if (d.base && list[0].min > 1) list.unshift({ min: 1, max: list[0].min - 1, type: PERCENT_OFF, value: 0, base: true });
    const each = (t) => money.format(discounted(price, t) / 100);
    const pct = (t) => discountText(price, t, money);
    const current = list.find((t) => quantity >= t.min && quantity <= t.max);
    const next = list.find((t) => t.min > quantity && !t.base);
    const cards = list.map((t) => {
      const range = t.max === Infinity ? fill(d.lastRange, { min: t.min }) : fill(t.base ? d.base : d.range, { min: t.min, max: t.max });
      const state = t === current ? ' is-active' : current && t.max < current.min ? ' is-passed' : '';
      return '<button type="button" class="kov-tier' + state + '" data-qty="' + t.min + '">' +
        (d.active ? '<span class="kov-tier__badge">' + esc(d.active) + '</span>' : '') +
        '<span class="kov-tier__range">' + esc(range) + '</span>' +
        '<span class="kov-tier__off">' + esc(t.base ? each(t) : fill(d.discount, { discount: pct(t) })) + '</span>' +
        (t.base ? '' : '<span class="kov-tier__price">' + esc(fill(d.price, { price: each(t) })) + '</span>') +
        '</button>';
    }).join('');
    let note = '';
    if (next && d.next) note = fill(d.next, { more: next.min - quantity, discount: pct(next), price: each(next) });
    else if (!next && current && !current.base && d.best) note = fill(d.best, { discount: pct(current) });
    const html = (d.title ? '<p class="kov-tiers__title">' + esc(d.title) + '</p>' : '') +
      '<div class="kov-tiers__list">' + cards + '</div>' +
      (note ? '<p class="kov-tiers__next">' + esc(note) + '</p>' : '');
    if (box.innerHTML !== html) box.innerHTML = html;
    if (box.hidden) box.hidden = false;
  };

  const update = () => {
    document.querySelectorAll('[data-kov-qty-price]').forEach(renderLine);
    document.querySelectorAll('[data-kov-tiers]').forEach(renderTiers);
  };
  let timer;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(update, 150); };

  // tapping a label sets the quantity to the start of that discount
  document.addEventListener('click', (e) => {
    const card = e.target.closest?.('[data-kov-tiers] .kov-tier');
    if (card) {
      const input = document.getElementById(card.closest('[data-kov-tiers]').dataset.quantity);
      if (input) {
        input.value = card.dataset.qty;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (e.target.closest?.('.btn-quantity')) schedule();
  });
  document.addEventListener('input', (e) => { if (e.target.matches?.('input[name="quantity"]')) schedule(); });
  // quantity changes, and variant changes (the rules can depend on the variant)
  document.addEventListener('change', (e) => { if (e.target.matches?.('input[name="quantity"], input[name="id"]')) schedule(); });

  // ignore changes made by our own lines (filling them in, moving the labels box)
  const ownNodes = (nodes) => nodes.length > 0 && [...nodes].every((n) => n.matches?.(OWN));
  const ours = (m) => !!(m.target.closest ? m.target : m.target.parentElement)?.closest?.(OWN) ||
    ownNodes(m.addedNodes) && (!m.removedNodes.length || ownNodes(m.removedNodes)) ||
    !m.addedNodes.length && ownNodes(m.removedNodes);
  new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach(hideAddedTables));
    if (mutations.some((m) => !ours(m))) schedule();
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
  hideAddedTables(document.body);
  schedule();
})();
